import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MobileSlideInMenu } from '@/components/layout/MobileSlideInMenu'
import { UserStatusDisplay } from '@/components/users'
import { User, UserStatus } from '@/types'
import { userService } from '@/services'
import { statusLabels, statusColors } from '@/lib/utils/user-status-utils'
import { cn } from '@/lib/utils/cn'

interface UserStatusSelectorProps {
  /** User object */
  user: User
  /** Callback function called when status is changed */
  onStatusChange?: (newStatus: UserStatus) => void
  /** Variant of the component */
  variant?: 'desktop' | 'mobile' | 'auto'
  /** Size of the status indicator */
  size?: 'sm' | 'md' | 'lg'
  /** Additional CSS classes */
  className?: string
  /** Whether to show the status label in the dropdown */
  showLabels?: boolean
  /** Custom refresh function to call after status update */
  onRefreshUser?: () => Promise<void>
}

/**
 * UserStatusSelector component for selecting and updating user status
 * Supports both desktop dropdown and mobile modal interfaces
 */
export function UserStatusSelector({
  user,
  onStatusChange,
  variant = 'auto',
  size = 'md',
  className = '',
  showLabels = true,
  onRefreshUser,
}: UserStatusSelectorProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)

  // Check if we're on desktop on mount and resize
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }

    checkIsDesktop()
    window.addEventListener('resize', checkIsDesktop)

    return () => window.removeEventListener('resize', checkIsDesktop)
  }, [])

  const handleStatusChange = async (newStatus: UserStatus) => {
    if (!user) return

    try {
      await userService.update(user.id, { status: newStatus } as Partial<typeof user>)
      toast.success(`Status updated to ${statusLabels[newStatus]}`)

      // Call optional refresh function
      if (onRefreshUser) {
        await onRefreshUser()
      }

      // Call optional callback
      onStatusChange?.(newStatus)

      // Close mobile menu if open
      setIsMobileMenuOpen(false)
    } catch {
      toast.error('Failed to update status')
    }
  }

  // Determine if we should use desktop or mobile variant
  const shouldUseDesktop = variant === 'desktop' || (variant === 'auto' && isDesktop)

  if (shouldUseDesktop) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`relative ${className}`}
            title={`Status: ${user.status}`}
          >
            <UserStatusDisplay user={user} variant="dot-only" size={size} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Set your status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {Object.values(UserStatus).map((status) => (
            <DropdownMenuItem
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`flex items-center gap-2 cursor-pointer ${
                user.status === status ? 'bg-accent' : ''
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${statusColors[status]}`} />
              {showLabels && (
                <span className={user.status === status ? 'font-medium' : ''}>
                  {statusLabels[status]}
                </span>
              )}
              {user.status === status && (
                <span className="ml-auto text-xs text-muted-foreground">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={`relative ${className}`}
        title={`Status: ${user.status}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        <UserStatusDisplay user={user} variant="dot-only" size={size} />
      </Button>

      <MobileSlideInMenu
        id="mobile-user-status-selector"
        heading="Set your status"
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        mode="top"
      >
        <MobileUserStatusSelector
          currentStatus={user.status}
          onStatusChange={handleStatusChange}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      </MobileSlideInMenu>
    </>
  )
}

/**
 * Mobile-specific user status selector component
 * Used internally by UserStatusSelector for mobile interfaces
 */
interface MobileUserStatusSelectorProps {
  /** Current user status */
  currentStatus: UserStatus
  /** Callback when status changes */
  onStatusChange: (status: UserStatus) => void
  /** Callback when menu should close */
  onClose: () => void
}

function MobileUserStatusSelector({
  currentStatus,
  onStatusChange,
  onClose,
}: MobileUserStatusSelectorProps) {
  const statusOptions = Object.values(UserStatus).map((status) => (
    <button
      key={status}
      onClick={() => {
        onStatusChange(status)
        onClose()
      }}
      className={cn(
        'flex items-center gap-4 w-full rounded-xl px-4 py-3 transition-colors active:scale-95',
        currentStatus === status
          ? 'bg-primary/10 text-primary font-medium'
          : 'text-foreground hover:bg-muted active:bg-muted'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center h-10 w-10 rounded-full transition-colors',
          currentStatus === status ? 'bg-primary text-primary-foreground' : 'bg-muted'
        )}
      >
        <div className={`w-2 h-2 rounded-full ${statusColors[status]}`} />
      </div>
      <span className="text-base">{statusLabels[status]}</span>
      {currentStatus === status && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
    </button>
  ))

  return <div className="space-y-1">{statusOptions}</div>
}
